<template>
  <div
    class="reference-list"
    :class="[`reference-list_${componentType.toLowerCase()}`]"
  >
    <template v-if="showScreenOnly">
      <slot name="screen" />
    </template>
    <template v-else>
      <div
        v-if="!hideHeader"
        class="reference-list__header"
      >
        <slot name="header" />
      </div>
      <Observer
        v-if="showList"
        :items="searchList"
        :last="isLast"
        :options="observerOptions"
        class="reference-list__list"
        @intersect="$emit('onScroll')"
      >
        <template #default="{ item, index }">
          <div
            :id="`reference-item_${index}`"
            :class="{ 'is-selected': index === selectedIndex }"
            class="reference-list__item"
          >
            <button
              type="button"
              class="reference-list__button"
              @click.stop="selectItem(item)"
            >
              <slot
                name="item"
                :option="item"
              />
            </button>
          </div>
        </template>
      </Observer>
      <div
        v-if="$wait.is('referenceSearch') || $wait.is('referenceSprintSearch')"
        class="reference-loading"
      >
        ...loading
      </div>
      <div
        v-else-if="showNotFound"
        class="reference-loading"
      >
        No results found.
      </div>
      <div
        v-if="!hideFooter"
        class="reference-list__footer"
      >
        <slot name="footer" />
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, getCurrentInstance, toRefs } from "vue"

import Observer from "@/components/Observer/index.vue"

import { ReferenceType } from "@/plugins/extensions/ReferenceType"
import { observerOptions } from "@/utils/helpers"

const props = withDefaults(
  defineProps<{
    searchList?: unknown[]
    componentType: ReferenceType
    selectedIndex?: number
    hideFooter?: boolean
    hideHeader?: boolean
    hideContent?: boolean
    isLast?: boolean
    showScreenOnly?: boolean
  }>(),
  {
    searchList: () => [],
    selectedIndex: 0,
    hideFooter: false,
    hideHeader: false,
    hideContent: false,
    isLast: true,
    showScreenOnly: false
  }
)

const {
  searchList,
  componentType,
  selectedIndex,
  hideFooter,
  hideHeader,
  hideContent,
  isLast,
  showScreenOnly
} = toRefs(props)

const emit = defineEmits<{
  (event: "onClick", item: unknown): void
  (event: "onScroll"): void
}>()

const { proxy } = getCurrentInstance()!

const wait = proxy?.$wait

function selectItem(item: unknown) {
  emit("onClick", item)
}

const showList = computed(() => {
  return searchList.value?.length !== 0 && !hideContent.value && !wait?.is("referenceSearch")
})

const showNotFound = computed(() => {
  return searchList.value?.length === 0 && !hideContent.value && !wait?.is("referenceSearch")
})
</script>
